/**
 * Черновики квизов ведущего в localStorage (И5, phase-2-plan §2.1).
 *
 * Архитектура «гибрид» (вариант A плана): редактирование живёт в браузере
 * (localStorage), публикация/проведение — на сервере через POST /api/games
 * (см. api.ts::createGameFromDraft). localStorage не нагружает сервер и не
 * порождает спам анонимных квизов; перенос между устройствами — экспорт/импорт
 * JSON-файла. В Фазе 3 черновик можно «опубликовать» в профиль пользователя тем
 * же JSON (мост миграции).
 *
 * Хранилище: индекс `kikayoo:drafts` (метаданные — для быстрого списка) + записи
 * `kikayoo:draft:<uuid>` (полное тело черновика). UUID — через crypto.randomUUID
 * (с fallback для старых окружений).
 *
 * Редакторская модель вопроса — дискриминированное объединение по `kind` с
 * правильным ответом (аналог серверного game.Question с *Def): choice хранит
 * correctIndex, open — accept, match — pairs, order — correctOrder. poll не имеет
 * правильного ответа. Конвертация в wire-payload без правильного ответа для
 * превью — draftQuestionToPreview; в полный wire-payload (с правильным) —
 * api.ts::encodeDraftQuiz.
 */
import { type Kind, type KindData, type QuestionPayload } from './protocol';

// ─── Редакторская модель вопроса ───────────────────────────────────────────────

/** Пара соответствия в черновике (↔ server game.MatchPair). */
export interface DraftMatchPair {
  left: string;
  right: string;
}

/**
 * Type-специфичное тело вопроса-черновика (дискриминант `kind`). Каждое хранит
 * правильный ответ, как серверная *Def-секция.
 */
export interface ChoiceDraftBody {
  kind: 'choice';
  options: string[];
  /** 0-based индекс правильного варианта. */
  correctIndex: number;
}
export interface PollDraftBody {
  kind: 'poll';
  options: string[];
}
export interface OpenDraftBody {
  kind: 'open';
  /** Допустимые (эталонные) ответы; сверка после нормализации на сервере. */
  accept: string[];
  /** Максимальная длина ответа игрока; опц. */
  maxLen?: number;
}
export interface MatchDraftBody {
  kind: 'match';
  pairs: DraftMatchPair[];
}
export interface OrderDraftBody {
  kind: 'order';
  items: string[];
  /** Верный порядок как перестановка индексов items. */
  correctOrder: number[];
}
/** Тело word-вопроса (облако слов, И8): правильного ответа нет — только maxLen. */
export interface WordDraftBody {
  kind: 'word';
  /** Максимальная длина слова игрока (рун); 1..200, серверный дефолт 40. */
  maxLen?: number;
}
/**
 * Тело multi-вопроса (мультивыбор, Фаза 14 эпик C) — ПЛОСКОЕ по конвенции
 * черновиков (решение владельца 2026-10-19, §2.1 спеки): без вложенной секции
 * multi:{…}; вложенная секция появляется только в wire-QuizPayload (api.ts).
 */
export interface MultiDraftBody {
  kind: 'multi';
  options: string[];
  /** Индексы правильных вариантов (0-based, уникальные; 1..n−1 при публикации). */
  correct: number[];
}
/**
 * Тело run-вопроса «Беги к углу» (Фаза 14, эпик B4) — ПЛОСКОЕ по конвенции
 * черновиков (решение владельца 2026-10-19, §3.1 спеки): {corners,
 * correctIndex} без вложенной секции run:{…} (она — только в wire-QuizPayload,
 * api.ts). Seed и игровые параметры НЕ редактируются: seed генерирует сервер
 * на каждый StartQuestion (§3.1), физика — фикс-константы (§3.6).
 */
export interface RunDraftBody {
  kind: 'run';
  /** Тексты 4 углов плитки (0 — левый-верхний, по часовой, §3.1). */
  corners: string[];
  /** Правильный угол 0..3 (анти-чит: на провод — только в reveal). */
  correctIndex: number;
}
export type DraftQuestionBody =
  | ChoiceDraftBody
  | PollDraftBody
  | OpenDraftBody
  | MatchDraftBody
  | OrderDraftBody
  | WordDraftBody
  | MultiDraftBody
  | RunDraftBody;

/** Редакторский вопрос: заголовок + длительность + изображение + блок кода + тип-тело с правильным ответом. */
export interface DraftQuestion {
  /** Локальный id (для keying/перемещения в списке). */
  id: string;
  title: string;
  /** Длительность, мс; 0/отсутствие = серверный дефолт (DefaultQuestionMs). */
  durationMs?: number;
  /** URL загруженного изображения вопроса (POST /api/uploads → /uploads/<hex>.jpg); опц. */
  image?: string;
  /**
   * Многострочный блок кода вопроса (Фаза 16, B4, additive по образцу image);
   * опц. — ровно один на вопрос, лимит limits.code_max_chars (руны).
   */
  code?: string;
  /** Язык блока кода из whitelist (реестр languages.ts); отсут./пусто => plaintext. */
  codeLang?: string;
  body: DraftQuestionBody;
}

/** Полный черновик квиза. */
export interface QuizDraft {
  id: string;
  title: string;
  questions: DraftQuestion[];
  updatedAt: number;
  /**
   * Привязка к серверному квизу (Фаза 12, B1): черновик, созданный правкой квиза
   * из кабинета, ссылается на его games.id. Наличие поля решает PUT/POST в
   * HostEditor (PUT /api/games/{gameId}); привязка персистентна — переживает
   * выход из редактора и прямой заход в /edit, поэтому повторные «Редактировать»
   * не плодят дубли квизов. Опционально (additive): старые localStorage-записи
   * без поля читаются как обычные непривязанные черновики (guard'ы пропускают).
   */
  gameId?: number;
}

/** Метаданные черновика для индекса (без тел вопросов). */
export interface DraftMeta {
  id: string;
  title: string;
  updatedAt: number;
  /** Число вопросов (для отображения в списке); опц., может отсутствовать у старых записей. */
  questionsCount?: number;
  /** Привязка к серверному квизу (B1); опц. — старый индекс читается без поля. */
  gameId?: number;
}

// ─── Хранилище localStorage ───────────────────────────────────────────────────

const DRAFTS_INDEX_KEY = 'kikayoo:drafts';
/** Отдельный ключ метки «последний открытый черновик» (0.12.1): хранится ВНЕ
 *  индекса — открытие черновика не мутирует updatedAt и порядок списка. */
const DRAFTS_LAST_OPEN_KEY = 'kikayoo:drafts:last-open';

/** Ключ localStorage для тела одного черновика. */
export function draftStorageKey(id: string): string {
  return `kikayoo:draft:${id}`;
}

/** Минимальный интерфейп хранилища (для мока в тестах и устойчивости в SSR). */
interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Возвращает localStorage, если он доступен. В приватном режиме / при переполнении
 * квоты доступ может бросать — тогда возвращаем null, и хранилище молча
 * деградирует (чтение → пусто, запись → no-op), не роняя редактор.
 */
function getStorage(): StorageLike | null {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return null;
  }
  try {
    const s = window.localStorage;
    s.setItem('__kky_probe__', '1');
    s.removeItem('__kky_probe__');
    return s;
  } catch {
    return null;
  }
}

/**
 * Перехватчик хранилища: по умолчанию window.localStorage. Переопределяется в
 * тестах через setDraftStorageForTests. Неэкспортируемая глобальная точка подмены
 * хранится в замыкании модуля.
 */
let storageOverride: StorageLike | null | undefined = undefined;

/**
 * Тестовый хук: подменяет хранилище черновиков (mock localStorage). null
 * эмулирует отсутствие localStorage. ВНИМАНИЕ: только для тестов.
 */
export function setDraftStorageForTests(storage: StorageLike | null): void {
  storageOverride = storage;
}

function storage(): StorageLike | null {
  if (storageOverride !== undefined) return storageOverride;
  return getStorage();
}

// ─── CRUD ─────────────────────────────────────────────────────────────────────

/** Список метаданных черновиков (упорядочен от новых к старым по updatedAt). */
export function listDrafts(): DraftMeta[] {
  const s = storage();
  if (!s) return [];
  const raw = s.getItem(DRAFTS_INDEX_KEY);
  const parsed = safeParseJSON(raw);
  if (!Array.isArray(parsed)) return [];
  return parsed.filter(isDraftMeta).sort((a, b) => b.updatedAt - a.updatedAt);
}

/** Полный черновик по id или null, если его нет (или тело повреждено). */
export function getDraft(id: string): QuizDraft | null {
  const s = storage();
  if (!s) return null;
  const parsed = safeParseJSON(s.getItem(draftStorageKey(id)));
  return isQuizDraft(parsed) ? normalizeDraft(parsed) : null;
}

/**
 * Сохраняет полный черновик и обновляет индекс. updatedAt обновляется до текущего
 * момента (автосохранение/явное сохранение). Возвращает сохранённый черновик.
 */
export function saveDraft(draft: QuizDraft): QuizDraft {
  const s = storage();
  const updated: QuizDraft = normalizeDraft({ ...draft, updatedAt: Date.now() });
  if (!s) return updated;
  s.setItem(draftStorageKey(updated.id), JSON.stringify(updated));
  const index = listDrafts().filter((m) => m.id !== updated.id);
  index.unshift({
    id: updated.id,
    title: updated.title,
    updatedAt: updated.updatedAt,
    questionsCount: updated.questions.length,
    // Привязка к серверному квизу (B1) сохраняется в индексе — по ней сайдбар
    // рисует бейдж, а HostEditor находит существующий черновик для переиспользования.
    gameId: updated.gameId,
  });
  index.sort((a, b) => b.updatedAt - a.updatedAt);
  s.setItem(DRAFTS_INDEX_KEY, JSON.stringify(index));
  return updated;
}

/** Удаляет черновик и его запись в индексе. Идемпотентен. */
export function deleteDraft(id: string): void {
  const s = storage();
  if (!s) return;
  s.removeItem(draftStorageKey(id));
  const index = listDrafts().filter((m) => m.id !== id);
  s.setItem(DRAFTS_INDEX_KEY, JSON.stringify(index));
  // Удалён последний открытый — метку стираем, чтобы bootstrap редактора не
  // искал несуществующий id (fallback на list[0] был бы и так, но чище без мусора).
  if (getLastOpenedDraftId() === id) {
    try {
      s.removeItem(DRAFTS_LAST_OPEN_KEY);
    } catch {
      /* хранилище недоступно — метка необязательна */
    }
  }
}

// ─── Метка «последний открытый» (0.12.1) ──────────────────────────────────────

/**
 * Запоминает id последнего ОТКРЫТОГО черновика (просмотр/редактирование).
 * Живёт в ОТДЕЛЬНОМ ключе localStorage, а не в updatedAt индекса: открытие
 * черновика не мутирует порядок списка — наверх его поднимает только saveDraft
 * (реальные правки/сохранение). Тихо no-op при недоступном хранилище.
 */
export function setLastOpenedDraft(id: string): void {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(DRAFTS_LAST_OPEN_KEY, id);
  } catch {
    /* квота/приватный режим — метка необязательна, редактор работает и без неё */
  }
}

/** id последнего открытого черновика или null (метки нет / хранилища нет). */
export function getLastOpenedDraftId(): string | null {
  const s = storage();
  if (!s) return null;
  const raw = s.getItem(DRAFTS_LAST_OPEN_KEY);
  return raw !== null && raw.length > 0 ? raw : null;
}

/**
 * Лимит числа черновиков в localStorage (на пользователя/устройство): бережёт квоту
 * браузера и не даёт списку разрастись. При достижении createDraft/importDraft
 * бросают Error('drafts_limit_reached') — HostEditor блокирует кнопку «Новый квиз»
 * и показывает подсказку «удалите старый черновик».
 */
export const MAX_DRAFTS = 20;

/** Создаёт пустой черновик, сохраняет и возвращает его. Бросает Error, если уже
 *  достигнут лимит числа черновиков (MAX_DRAFTS). */
export function createDraft(title = 'Новый квиз'): QuizDraft {
  if (listDrafts().length >= MAX_DRAFTS) {
    throw new Error('drafts_limit_reached');
  }
  const draft: QuizDraft = {
    id: genId(),
    title,
    questions: [],
    updatedAt: Date.now(),
  };
  return saveDraft(draft);
}

// ─── Фабрики вопросов ─────────────────────────────────────────────────────────

/** Новое тело вопроса заданного типа с разумными значениями по умолчанию. */
export function makeDraftBody(kind: Kind): DraftQuestionBody {
  switch (kind) {
    case 'choice':
      return { kind: 'choice', options: ['', ''], correctIndex: 0 };
    case 'poll':
      return { kind: 'poll', options: ['', ''] };
    case 'open':
      return { kind: 'open', accept: [''], maxLen: 120 };
    case 'match':
      return {
        kind: 'match',
        pairs: [
          { left: '', right: '' },
          { left: '', right: '' },
        ],
      };
    case 'order':
      return { kind: 'order', items: ['', ''], correctOrder: [0, 1] };
    case 'word':
      return { kind: 'word', maxLen: 40 };
    case 'multi':
      return { kind: 'multi', options: ['', ''], correct: [0] };
    case 'run':
      // 4 пустых угла ровно (без добавления/удаления — §3.5); correctIndex 0.
      return { kind: 'run', corners: ['', '', '', ''], correctIndex: 0 };
  }
}

/** Новый вопрос заданного типа (с уникальным id) для добавления в черновик. */
export function makeDraftQuestion(kind: Kind): DraftQuestion {
  return { id: genId(), title: '', body: makeDraftBody(kind) };
}

/**
 * Возвращает копию вопроса с заменой типа тела (поля title/durationMs/id
 * сохраняются). Используется при переключении типа в редакторе.
 */
export function withDraftKind(question: DraftQuestion, kind: Kind): DraftQuestion {
  return {
    id: question.id,
    title: question.title,
    durationMs: question.durationMs,
    image: question.image,
    // Блок кода — свойство вопроса (не тела), переживает смену типа как image.
    code: question.code,
    codeLang: question.codeLang,
    body: makeDraftBody(kind),
  };
}

// ─── Экспорт / импорт JSON ────────────────────────────────────────────────────

/** Сериализует черновик в красивый JSON (для экспорта в файл). */
export function serializeDraft(draft: QuizDraft): string {
  return JSON.stringify(draft, null, 2);
}

/**
 * Скачивает черновик как JSON-файл (триггер browser download). No-op вне браузера.
 */
export function downloadDraft(draft: QuizDraft): void {
  if (
    typeof document === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return;
  }
  const blob = new Blob([serializeDraft(draft)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = slugify(draft.title || 'quiz') + '.json';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Парсит JSON-текст черновика (один квиз) и возвращает валидный QuizDraft с НОВЫМ
 * id: импорт трактуется как копия, чтобы не затереть существующий черновик с тем
 * же id. Бросает Error при невалидной схеме.
 */
export function parseDraftJSON(text: string): QuizDraft {
  const parsed = safeParseJSON(text);
  if (!isQuizDraft(parsed)) {
    throw new Error('Некорректный формат черновика: ожидается квиз с вопросами');
  }
  // Импорт = копия: новый id и БЕЗ привязки к серверному квизу (gameId
  // сбрасывается) — публикация копии честно создаёт новую игру (POST), а не
  // перезаписывает исходный квиз исходного черновика (B1).
  return { ...normalizeDraft(parsed), id: genId(), updatedAt: Date.now(), gameId: undefined };
}

/**
 * Импортирует черновик из JSON-текста: парсит, валидирует, присваивает новый id,
 * сохраняет в localStorage и возвращает. Бросает Error при невалидной схеме.
 */
export function importDraft(text: string): QuizDraft {
  const draft = parseDraftJSON(text);
  if (listDrafts().length >= MAX_DRAFTS) {
    throw new Error('drafts_limit_reached');
  }
  return saveDraft(draft);
}

// ─── Вставка вопросов из JSON (Фаза 12, D4) ───────────────────────────────────

/** Структурированная ошибка разбора JSON-вставки (для инлайн-сообщения модала). */
export interface DraftQuestionsParseError {
  /**
   * parse — битый JSON-синтаксис; shape — не объект/не массив объектов;
   * empty — пустой массив; question — невалидная структура вопроса.
   */
  kind: 'parse' | 'shape' | 'empty' | 'question';
  /** 0-based индекс невалидного элемента (kind='question'). */
  index?: number;
  /** Поле первого дефекта (kind='question'), напр. body.correctIndex. */
  field?: string;
}

export type DraftQuestionsParseResult =
  { ok: true; questions: DraftQuestion[] } | { ok: false; error: DraftQuestionsParseError };

/**
 * Разбирает JSON-вставку вопросов (D4): один объект вопроса или массив
 * объектов в формате предпросмотра/экспорта (DraftQuestion из serializeDraft).
 * Валидация ДО вставки — строгий структурный разбор по типам
 * choice/poll/open/match/order/word; невалидная структура НИКОГДА не
 * возвращается как ok (мусор отбрасывается на входе). Каждый вопрос получает
 * СВЕЖИЙ id через genId (вставка — новые сущности, конфликт/подмена
 * существующих id черновика исключены); лишние неизвестные поля отсекаются
 * normalizeQuestion. Ошибки — структурированные (индекс + поле), чтобы
 * редактор показал точное инлайн-сообщение, а кнопка «Вставить» осталась
 * disabled (лимит questionsPerQuiz проверяет сам редактор — ему виден текущий
 * размер черновика).
 */
export function parseDraftQuestionsJSON(text: string): DraftQuestionsParseResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: { kind: 'parse' } };
  }
  const items: unknown[] = Array.isArray(parsed) ? parsed : [parsed];
  if (!items.every(isRecord)) {
    return { ok: false, error: { kind: 'shape' } };
  }
  if (items.length === 0) {
    return { ok: false, error: { kind: 'empty' } };
  }
  const questions: DraftQuestion[] = [];
  for (let i = 0; i < items.length; i++) {
    const field = draftQuestionFieldError(items[i]);
    if (field !== null) {
      return { ok: false, error: { kind: 'question', index: i, field } };
    }
    // Структура проверена только что (field === null) — двойной каст переводит
    // Record<string, unknown> обратно в DraftQuestion для нормализации.
    const valid = items[i] as unknown as DraftQuestion;
    questions.push(normalizeQuestion({ ...valid, id: genId() }));
  }
  return { ok: true, questions };
}

// ─── Превью вопроса (без правильного ответа — анти-чит) ────────────────────────

/**
 * Строит превью-QuestionPayload (для существующих под-view components/views/*)
 * БЕЗ правильного ответа: только kind + kindData (варианты/пары/элементы).
 * reveal остаётся null на стороне UI, поэтому верный ответ не подсветится —
 * анти-чит сохраняется. index/total — для бейджа «Вопрос X из N».
 */
export function draftQuestionToPreview(
  question: DraftQuestion,
  index: number,
  total: number,
  now: number = Date.now(),
): QuestionPayload {
  const durationMs = question.durationMs && question.durationMs > 0 ? question.durationMs : 20000;
  return {
    questionId: index + 1,
    title: question.title || 'Без заголовка',
    durationMs,
    startedAt: now,
    index,
    total,
    kind: question.body.kind,
    kindData: draftBodyToKindData(question.body),
    // image опционально — переносим в превью только при непустом URL (иначе undefined).
    ...(question.image && question.image.length > 0 ? { image: question.image } : {}),
    // Блок кода (Фаза 16, B4): превью рендерит его тем же CodeBlock, что и
    // живой QuestionView (анти-чит не затрагивается — code не является ответом).
    ...(question.code && question.code.length > 0 ? { code: question.code } : {}),
    ...(question.code && question.code.length > 0 && question.codeLang
      ? { codeLang: question.codeLang }
      : {}),
  };
}

/** Редакторское тело → kindData без правильного ответа (для превью). */
function draftBodyToKindData(body: DraftQuestionBody): KindData {
  switch (body.kind) {
    case 'choice':
    case 'poll':
      return { options: body.options.slice() };
    case 'open':
      return body.maxLen !== undefined ? { maxLen: body.maxLen } : {};
    case 'match':
      return {
        left: body.pairs.map((p) => p.left),
        right: body.pairs.map((p) => p.right),
      };
    case 'order':
      return { items: body.items.slice() };
    case 'word':
      return body.maxLen !== undefined ? { maxLen: body.maxLen } : {};
    case 'multi':
      // Анти-чит превью: только варианты, correct не попадает (как choice).
      return { options: body.options.slice() };
    case 'run':
      // Анти-чит превью: только углы; seed/params выдаёт сервер в реальной
      // игре (превью-плитка без дронов — статическая миниатюра, §3.5).
      return { corners: body.corners.slice() };
  }
}

// ─── Утилиты ──────────────────────────────────────────────────────────────────

/** Генерирует id черновика/вопроса: crypto.randomUUID с fallback. */
export function genId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'd-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
}

/** slug для имени файла экспорта (латиница/кириллица/цифры). */
function slugify(s: string): string {
  const cleaned = s
    .trim()
    .toLowerCase()
    .replace(/[^a-zа-яё0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '');
  return cleaned || 'quiz';
}

/** Безопасный JSON.parse: возвращает null при невалидном/пустом входе. */
function safeParseJSON(raw: string | null | undefined): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Нормализация черновика после чтения/импорта: гарантирует наличие всех полей и
 * глубокие копии массивов (чтобы мутации в UI не задели сохранённое). Не валидирует
 * схему — для этого есть isQuizDraft.
 */
function normalizeDraft(d: QuizDraft): QuizDraft {
  return {
    id: d.id,
    title: typeof d.title === 'string' ? d.title : '',
    updatedAt: typeof d.updatedAt === 'number' ? d.updatedAt : Date.now(),
    questions: Array.isArray(d.questions) ? d.questions.map(normalizeQuestion) : [],
    // Привязка к серверному квизу (B1): переносится только валидным числом;
    // старые записи без поля (или с мусором) читаются как непривязанные.
    ...(typeof d.gameId === 'number' && Number.isFinite(d.gameId) ? { gameId: d.gameId } : {}),
  };
}

function normalizeQuestion(q: DraftQuestion): DraftQuestion {
  return {
    id: q.id,
    title: typeof q.title === 'string' ? q.title : '',
    durationMs: q.durationMs,
    // Сохраняем только строковый URL (включая пустой при очистке); иначе undefined.
    image: typeof q.image === 'string' ? q.image : undefined,
    // Блок кода (Фаза 16, B4): как image — переносим только строковые значения,
    // старые черновики без полей остаются валидными (undefined).
    code: typeof q.code === 'string' ? q.code : undefined,
    codeLang: typeof q.codeLang === 'string' ? q.codeLang : undefined,
    body: q.body,
  };
}

// ─── Тайп-гарды (валидация схемы импорта) ─────────────────────────────────────

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function isString(v: unknown): v is string {
  return typeof v === 'string';
}
function isNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}
function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every(isString);
}
function isNumberArray(v: unknown): v is number[] {
  return Array.isArray(v) && v.every(isNumber);
}
function isDraftMatchPair(v: unknown): v is DraftMatchPair {
  if (!isRecord(v)) return false;
  return isString(v.left) && isString(v.right);
}

/**
 * Поле первого структурного дефекта вопроса (Фаза 12, D4): null, если вопрос
 * валиден. Проверяет общие поля (id-тип/title/durationMs/image) и тип-тело
 * body по kind — правила 1:1 прежнему isDraftQuestionBody. id здесь ОПЦИОНАЛЕН
 * (строка, если присутствует): вопросы JSON-вставки получают свежие id через
 * genId, требовать его в поле ввода незачем. Возвращённое поле попадает в
 * инлайн-сообщение модала вставки («Вопрос N: поле body.correctIndex»).
 */
function draftQuestionFieldError(v: unknown): string | null {
  if (!isRecord(v)) return 'question';
  if (v.id !== undefined && !isString(v.id)) return 'id';
  if (!isString(v.title)) return 'title';
  if (v.durationMs !== undefined && !isNumber(v.durationMs)) return 'durationMs';
  if (v.image !== undefined && !isString(v.image)) return 'image';
  // Блок кода (Фаза 16, B4): строки опциональны (образец справочника несёт их
  // верхнего уровня); состав codeLang не гардим — его валидирует сервер (whitelist).
  if (v.code !== undefined && !isString(v.code)) return 'code';
  if (v.codeLang !== undefined && !isString(v.codeLang)) return 'codeLang';
  if (!isRecord(v.body)) return 'body';
  const b = v.body;
  switch (b.kind) {
    case 'choice':
      if (!isStringArray(b.options)) return 'body.options';
      if (!isNumber(b.correctIndex)) return 'body.correctIndex';
      return null;
    case 'poll':
      return isStringArray(b.options) ? null : 'body.options';
    case 'open':
      if (!isStringArray(b.accept)) return 'body.accept';
      if (b.maxLen !== undefined && !isNumber(b.maxLen)) return 'body.maxLen';
      return null;
    case 'match':
      if (!Array.isArray(b.pairs) || !b.pairs.every(isDraftMatchPair)) return 'body.pairs';
      return null;
    case 'order':
      if (!isStringArray(b.items)) return 'body.items';
      if (!isNumberArray(b.correctOrder)) return 'body.correctOrder';
      return null;
    case 'word':
      if (b.maxLen !== undefined && !isNumber(b.maxLen)) return 'body.maxLen';
      return null;
    case 'multi':
      if (!isStringArray(b.options)) return 'body.options';
      if (!isNumberArray(b.correct)) return 'body.correct';
      return null;
    case 'run':
      if (!isStringArray(b.corners)) return 'body.corners';
      if (!isNumber(b.correctIndex)) return 'body.correctIndex';
      return null;
    default:
      return 'body.kind';
  }
}

/**
 * Строгий страж одного вопроса-черновика. Публичный с Фазы 12 (D4; ранее —
 * приватная деталь isQuizDraft): общие поля + тип-тело по kind, мусор
 * отбрасывается. id ОБЯЗАТЕЛЕН (строка) — как в сохранённых черновиках;
 * для JSON-вставки без id есть parseDraftQuestionsJSON.
 */
export function isDraftQuestion(v: unknown): v is DraftQuestion {
  return isRecord(v) && isString(v.id) && draftQuestionFieldError(v) === null;
}

/** Страж полного черновика (используется при чтении и импорте). */
export function isQuizDraft(v: unknown): v is QuizDraft {
  if (!isRecord(v)) return false;
  return (
    isString(v.id) &&
    isString(v.title) &&
    Array.isArray(v.questions) &&
    v.questions.every(isDraftQuestion) &&
    isNumber(v.updatedAt) &&
    // gameId опционален (additive, B1): старые записи без поля валидны; мусор
    // (не число) в поле отвергается, чтобы привязка не «случайно» появилась.
    (v.gameId === undefined || isNumber(v.gameId))
  );
}

function isDraftMeta(v: unknown): v is DraftMeta {
  if (!isRecord(v)) return false;
  return (
    isString(v.id) &&
    isString(v.title) &&
    isNumber(v.updatedAt) &&
    (v.gameId === undefined || isNumber(v.gameId))
  );
}

/**
 * Существующий черновик, привязанный к серверному квизу (B1), или null.
 * «Редактировать» квиза из кабинета переиспользует этот черновик (актуализируя
 * его содержимое с сервера), а не создаёт копию — дубликаты не плодятся.
 */
export function findDraftByGameId(gameId: number): QuizDraft | null {
  const meta = listDrafts().find((m) => m.gameId === gameId);
  return meta ? getDraft(meta.id) : null;
}
